import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { PaymentTransaction } from 'app/core/models/paymentTransaction';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PaymentTransactionsService } from 'app/core/services/payment-transactions.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, of, ReplaySubject, timer } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { tableEventChange } from '../table-utils/classes/tableChangeEvent';
import { columnType } from '../table-utils/enums/columnType';


export class PaymentTransactionData {
  merchantName : string;
  paymentTransaction : PaymentTransaction;
}

@Component({
  selector: 'app-payment-transaction-page',
  templateUrl: './payment-transaction-page.component.html',
  styleUrls: ['./payment-transaction-page.component.css']
})
export class PaymentTransactionPageComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy {


  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;

  paymentTransactions : Map<string, PaymentTransactionData> = new Map<string, PaymentTransactionData>();
  statusItems: Array<String> = Array<String>();
  statusMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();

  payTxProviderItems: Array<String> = Array<String>();

  columns: {
    data : {
      title: string;
      type: columnType
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0,
    sort: "createdDate,DESC",
    size: 20
  };

  criteria : PaymentTransaction = new PaymentTransaction();

  columnPropertyMapping = {
    "CREATION_DATE" : "createdDate",
    "AMOUNT" : "capturedAmount",
    "MERCHANT_NAME" : "merchantId",
    "PAYMENT_TRANSACTION_PROVIDER" : "paymentTransactionProvider",
    "TRANSACTION_EXTERNAL_ID" : "externalId",
    "STATUS" : "status",
  };

  searchForm : FormGroup = new FormGroup({
    id: new FormControl(),
    from: new FormControl(),
    to: new FormControl(),
    walletId: new FormControl(),
    transactionReference: new FormControl(),
    merchantId: new FormControl(),
    merchantRef: new FormControl(),
    status: new FormControl(),
    paymentTransactionProvider : new FormControl()
  });

  constructor(
    private paymentTransactionService: PaymentTransactionsService,
    private translateService: TranslateService,
    private roleGuard: AuthenticatedRouteGuard,
    private notifService: NotifMessageService,
    private keycloakService: KeycloakService,
    protected supplierService: SupplierService,
    protected merchantService: MerchantService,
    protected posService: PosService,
    protected log: NGXLogger,
    router: Router
  ) { super(supplierService, merchantService, posService, log, router); }


  ngOnInit() {
    this.columns = {
      data : [
      { title: "CREATION_DATE", type: columnType.date },
      { title: "AMOUNT", type: columnType.text },
      { title: "MERCHANT_NAME", type: columnType.text },
      { title: "PAYMENT_TRANSACTION_PROVIDER", type: columnType.text },
      { title: "TRANSACTION_EXTERNAL_ID", type: columnType.text },
      { title: "STATUS", type: columnType.chip }
    ],
    clickable : true
    };

    this.getMerchants();
    this.getAllPayTxProvider();
    this.initializeStatus();
    this.translateService.onLangChange.subscribe(() => {
      this.initializeStatus();
    });

    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('PAYMENT_TRANSACTION_SEARCH')) {
          this.loadData();
        }
      }
    });
  }

  formatTransaction(paymentTransaction: PaymentTransaction, merchantName : string) {
    this.paymentTransactions.set(paymentTransaction.id, {paymentTransaction, merchantName})
    return {
      id: paymentTransaction.id,
      CREATION_DATE: paymentTransaction.createdDate,
      AMOUNT: paymentTransaction.capturedAmount ? [this.getDecimalValue(paymentTransaction.capturedAmount), paymentTransaction.currency].join(" ") : "N/A",
      MERCHANT_NAME: merchantName,
      PAYMENT_TRANSACTION_PROVIDER: paymentTransaction.paymentTransactionProvider ? paymentTransaction.paymentTransactionProvider : "N/A",
      TRANSACTION_EXTERNAL_ID: paymentTransaction.externalId ? paymentTransaction.externalId : "N/A",
      STATUS: this.getTxStatus(paymentTransaction),
    };
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  loadData(isLoadingSearch : boolean = false) : void{
    this.isLoadingResults = true;
    this.paymentTransactionService
    .findByCriteria(this.criteria, this.pager)
    .pipe(
      tap(paymentTransactions => this.totalLength$.next(paymentTransactions.totalElements)),
      switchMap(paymentTransactions => {
        if (paymentTransactions.content.length !== 0) {
          const paymentTransactions$ = paymentTransactions.content.map(paymentTransaction => {
            const merchantName$ = this.getMerchantName(paymentTransaction.merchantId);
            return merchantName$.pipe(
              map(merchantName => {
                  return this.formatTransaction(paymentTransaction, merchantName)
              }
              ));
          });
          return combineLatest(paymentTransactions$)
        }
        if (isLoadingSearch) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
        return of([]);
      }),
      map(paymentTransactions => {
        this.isLoadingResults = false;
        if (paymentTransactions === null) {
          return [];
        }
        return paymentTransactions;
      }),
    ).subscribe(paymentTransactions =>  {
      this.dataSubject$.next(paymentTransactions);
    })
  }

  download() {
    const pager = {
      page: 0,
      sort: this.pager.sort,
      size: 10000
    };
    this.paymentTransactionService.exportByCriteria(this.criteria, pager).subscribe(
      items => {
        if (items.totalElements > 10000) {
          this.notifService.notifyErrorWithoutDetail('EXPORT_NOTE');
        }
        this.paymentTransactionService.downloadFile(items.content, 'Export_results');
      }
    );
  }

  getAllPayTxProvider() {
    let array = [];
    this.paymentProviderList.forEach(element =>
        array.push(element));
    this.payTxProviderItems = array;
  }

  detailsModal(transactionId : string): void {
    const transactionData = this.paymentTransactions.get(transactionId);
    this.notifService.emitModalWithDetails("PAYMENT_TRANSACTION_DETAILS", [transactionData.paymentTransaction, transactionData.merchantName]);
  }

  getTxStatus(tx: PaymentTransaction) {
    let paymentTransactionStatusHistory = tx.paymentTransactionStatusHistory;
    if (paymentTransactionStatusHistory != null && paymentTransactionStatusHistory != undefined && paymentTransactionStatusHistory.length > 0) {
      var recentDate = new Date(Math.max.apply(null, paymentTransactionStatusHistory.map(e => {
        return new Date(e.createdDate);
      })));
      var recentHistory = paymentTransactionStatusHistory.filter(e => {
        var d = new Date(e.createdDate);
        return d.getTime() == recentDate.getTime();
      })[0];

      return recentHistory.status;
    } else {
      return "N/A";
    }
  }

  initializeStatus() : void{
    let array = [];
    this.getTransactionStatus().forEach(element =>
      this.translateService.get(element).subscribe((trad: string) => {
        array.push(trad);
        this.statusMap.push({ element: element, traduction: trad });
        array.sort((a: string, b: string) => {
          return this.translateService.instant(a).localeCompare(this.translateService.instant(b))
        });
      }));
    this.statusItems = array;
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex,
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "createdDate,DESC",
      size: event.pageSize
    }
    this.loadData();
  }

  search() : void {
    this.criteria = new PaymentTransaction();
    for (let field in this.searchForm.value) {
        if (!this.searchForm.value[field]) {
          delete this.searchForm.value[field];
        } else {
          if (field === "status") {
            let trad = this.statusMap.find(Type => Type.traduction == this.searchForm.value[field]);
            this.criteria['paymentTransactionStatusHistory[0].status'] = (typeof trad !== "undefined") ? trad.element : this.searchForm.value[field];
          } else if (field === "from" || field === "to") {
            const fieldValue = this.searchForm.value[field];
            const date = new Date(fieldValue);
            date.setSeconds(0, 0);
            this.criteria[field] = date.toISOString();
          } else {
            this.criteria[field] = this.searchForm.value[field]
          }
        }
    }
    if (this.criteria && this.criteria["from"] && this.criteria["to"] && this.criteria["from"] > this.criteria["to"]) {
      this.notifService.notifyErrorWithoutDetail("ERROR_DATE");
      return;
    }
    this.loadData(true);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new PaymentTransaction();
    this.loadData();
  }
}
